import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, Tiptap } from '@tiptap/react';
import { getRichTextExtensions, normalizeMediaNodesToInline } from '../../../lib/tiptap';
import type { TiptapJSON } from '../../../lib/tiptap';
import { cn } from '../../../lib/utils';
import type { KitTranslateFn } from '../../../lib/kitTranslate';
import { RichTextToolbar } from './RichTextToolbar';
import type { RichTextToolbarFeature } from './RichTextToolbar';
import { TableBubbleMenu } from './TableBubbleMenu';
import { MediaBubbleMenu } from './MediaBubbleMenu';
import './tiptap.css';

export interface RichTextEditorProps {
    id?: string;
    value?: TiptapJSON;
    onChange?: (data: TiptapJSON) => void;
    placeholder?: string;
    readOnly?: boolean;
    minHeight?: number;
    className?: string;
    error?: boolean;
    translate?: KitTranslateFn;
    /** When set, only the listed features show toolbar buttons. Undefined = all. */
    features?: RichTextToolbarFeature[];
    /** Hide the formatting toolbar entirely. Content is still editable. */
    hideToolbar?: boolean;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
    id,
    value,
    onChange,
    placeholder = 'Start typing or press Tab for more options...',
    readOnly = false,
    minHeight = 200,
    className,
    error = false,
    translate,
    features,
    hideToolbar = false,
}) => {
    // EAA-1584: Track the JSON we last emitted so the value->editor sync effect
    // can recognise its own echoes. A single "skip next" boolean was too fragile
    // for the rapid burst of updates fired during a table column-resize drag: a
    // stale echo would slip through and call setContent(), resetting the document
    // mid-drag and making the table flash / the resize break.
    const lastEmittedRef = useRef<string | null>(null);
    const [tableBubbleMenuReady, setTableBubbleMenuReady] = useState(false);

    const handleUpdate = useCallback(
        ({ editor }: { editor: { getJSON: () => TiptapJSON } }) => {
            const json = editor.getJSON();
            lastEmittedRef.current = JSON.stringify(json);
            onChange?.(json);
        },
        [onChange]
    );

    const editor = useEditor(
        {
            extensions: getRichTextExtensions(placeholder),
            // EAA-1736: migrate legacy block-positioned media to inline-in-paragraph.
            content: value
                ? normalizeMediaNodesToInline(value)
                : { type: 'doc', content: [{ type: 'paragraph' }] },
            editable: !readOnly,
            onUpdate: handleUpdate,
            editorProps: {
                attributes: {
                    class: 'tiptap-editor-content focus:outline-none min-h-[100px]',
                    style: `min-height: ${minHeight}px`,
                },
            },
        },
        [placeholder, readOnly]
    );

    useEffect(() => {
        if (!editor || !value) return;
        // EAA-1736: compare against the normalized form the editor actually holds,
        // so migrating legacy block media doesn't trigger a setContent loop.
        const normalizedValue = normalizeMediaNodesToInline(value);
        const incoming = JSON.stringify(normalizedValue);
        // Echo of our own edit (incl. every intermediate frame of a resize drag).
        if (incoming === lastEmittedRef.current) return;
        // Already in sync — nothing to push.
        if (incoming === JSON.stringify(editor.getJSON())) return;
        // EAA-1584: never clobber the document while the user is actively
        // interacting (typing or dragging a column border). External prop
        // echoes arriving mid-interaction are stale; applying them resets the
        // ProseMirror state and interrupts the column-resize drag. Genuine
        // external content swaps happen while the editor is blurred.
        if (editor.isFocused) return;
        editor.commands.setContent(normalizedValue, { emitUpdate: false });
        lastEmittedRef.current = incoming;
    }, [editor, value]);

    useEffect(() => {
        if (!editor || readOnly) {
            setTableBubbleMenuReady(false);
            return;
        }
        let cancelled = false;
        let innerRaf = 0;
        const outerRaf = requestAnimationFrame(() => {
            innerRaf = requestAnimationFrame(() => {
                if (!cancelled) {
                    setTableBubbleMenuReady(true);
                }
            });
        });
        return () => {
            cancelled = true;
            cancelAnimationFrame(outerRaf);
            cancelAnimationFrame(innerRaf);
            setTableBubbleMenuReady(false);
        };
    }, [editor, readOnly]);

    if (!editor) return null;

    return (
        <div
            id={id}
            className={cn(
                'rich-text-editor w-full rounded-lg border bg-card px-3 py-2 shadow-sm transition-colors duration-200',
                'focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-offset-background',
                error
                    ? 'border-destructive focus-within:border-destructive focus-within:ring-destructive/35'
                    : 'border-border-surface/45 focus-within:border-primary/40 focus-within:ring-ring/45',
                className
            )}
        >
            <Tiptap editor={editor}>
                {!hideToolbar && <RichTextToolbar translate={translate} features={features} />}
                {!hideToolbar && tableBubbleMenuReady ? <TableBubbleMenu translate={translate} /> : null}
                {/* EAA-1736: layout toolbar for a selected image/audio/embed. */}
                {!readOnly && tableBubbleMenuReady ? <MediaBubbleMenu translate={translate} /> : null}
                <Tiptap.Content className="tiptap-content px-2 py-2 transition-colors duration-200" />
            </Tiptap>
        </div>
    );
};

RichTextEditor.displayName = 'RichTextEditor';
